import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { CopyableText } from "components/CopyableText";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { gql } from "generated-graphql";
import {
  DenialType,
  DeniedFacilitiesQuery,
  DeniedFacilitiesQueryVariables,
  TierIiReportKind,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { prettyPrintDate } from "util/dates";
import { prettyPrintFacility } from "./utils";
import { parseDenialReasonsToText } from "./util.parseDenialReasonsToText";

const DENIED_FACILITIES_QUERY = gql(`
  query DeniedFacilities(
    $search: String
    $sort: [SortModel!]
    $page: Int
    $pageSize: Int
    $reportKind: TierIIReportKind
  ) {
    deniedFacilities(
      search: $search
      sort: $sort
      page: $page
      pageSize: $pageSize
      reportKind: $reportKind
    ) {
      items {
        facility {
          id
          name
          state
          streetAddress1
          streetAddress2
          city
          zip
          customerFacilityId
        }
        denialReasons {
          type
          configRule {
            id
            type
            value
            notes
            createdAt
            createdBy
            updatedAt
            updatedBy
          }
        }
        tenant {
          id
          name
        }
        partnerTenant {
          id
          name
        }
        ruleUpdatedAt
        ruleUpdatedBy
      }
      count
    }
  }
`);

type DeniedFacility =
  DeniedFacilitiesQuery["deniedFacilities"]["items"][number];

export function DeniedFacilities() {
  const [reportKind, setReportKind] = useState<TierIiReportKind>(
    TierIiReportKind.Annual
  );

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Denied Facilities",
    },
  ]);

  const columns: OmnisearchGridColDef<DeniedFacility>[] = [
    {
      field: "partnerTenantId",
      headerName: "Partner Tenant ID",
      renderCell: ({ row }) => {
        return <CopyableText>{row.partnerTenant?.id}</CopyableText>;
      },
    },
    {
      field: "partnerTenant",
      headerName: "Partner Tenant",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Tooltip title={row.partnerTenant?.id}>
            <Typography variant="body2" sx={{ py: 0.5 }}>
              {row.partnerTenant?.name}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "tenant",
      headerName: "Organization",
      flex: 1,
      filterKeyType: "multipleTenant",
      renderCell: ({ row }) => {
        return (
          <Tooltip title={row.tenant?.id}>
            <Typography variant="body2" sx={{ py: 0.5 }}>
              {row.tenant?.name}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "tenantId",
      headerName: "Tenant ID",
      renderCell: ({ row }) => {
        return <CopyableText>{row.tenant?.id}</CopyableText>;
      },
    },
    {
      field: "facilityId",
      headerName: "Facility ID",
      renderCell: ({ row }) => {
        return <CopyableText>{row.facility?.id}</CopyableText>;
      },
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Tooltip
            title={`${row.facility?.streetAddress1} ${row.facility?.streetAddress2} ${row.facility?.city}, ${row.facility?.state} ${row.facility?.zip}`}
          >
            <Typography variant="body2" sx={{ py: 0.5 }}>
              {prettyPrintFacility(row.facility)}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "state",
      headerName: "State",
      renderCell: ({ row }) => {
        return (
          <Typography variant="body2" sx={{ py: 0.5 }}>
            {row.facility?.state}
          </Typography>
        );
      },
    },
    {
      field: "denialReason",
      headerName: "Denial Reason",
      flex: 2,
      renderCell: ({ row }) => {
        return (
          <Stack gap={0.5}>
            {parseDenialReasonsToText(row).map((reason) => (
              <Typography key={reason} variant="body2" sx={{ py: 0.5 }}>
                {reason}
              </Typography>
            ))}
          </Stack>
        );
      },
      enumValues: Object.values(DenialType),
      filterKeyType: "enum",
    },
    {
      field: "lastUpdatedAt",
      headerName: "Last Updated",
      renderCell: ({ row }) => {
        return (
          <Typography variant="body2" sx={{ py: 0.5 }}>
            {row.ruleUpdatedAt ? prettyPrintDate(row.ruleUpdatedAt) : ""}
          </Typography>
        );
      },
      filterKeyType: "date",
    },
    {
      field: "lastUpdatedBy",
      headerName: "Last Updated By",
      renderCell: ({ row }) => {
        return (
          <Typography variant="body2" sx={{ py: 0.5 }}>
            {row.ruleUpdatedBy}
          </Typography>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      renderCell: ({ row }) => {
        return (
          <Stack>
            {row.denialReasons?.map((reason) => (
              <TruncateTypography
                variant="body2"
                showTooltip={true}
                sx={{ py: 0.5 }}
              >
                {reason.configRule?.notes}
              </TruncateTypography>
            ))}
          </Stack>
        );
      },
      filterable: false,
    },
  ];

  return (
    <Box sx={{ pt: 3 }}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="report-kind-label">Report Type</InputLabel>
          <Select
            labelId="report-kind-label"
            id="report-kind-select"
            value={reportKind}
            label="Report Type"
            onChange={(e) => setReportKind(e.target.value as TierIiReportKind)}
          >
            <MenuItem value={TierIiReportKind.Annual}>Annual</MenuItem>
            <MenuItem value={TierIiReportKind.Revision}>Revision</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <OmnisearchDataGrid
        persistenceKey="denied-facilities"
        columns={columns}
        dataQuery={DENIED_FACILITIES_QUERY}
        dataQueryVariables={{ reportKind }}
        getItems={(data) => data.deniedFacilities.items}
        getCount={(data) => data.deniedFacilities.count}
        getRowId={(row) => row.facility.id}
        rowHeight={"auto"}
        isURLDriven
        columnVisibilityModel={{
          facilityId: false,
          tenantId: false,
          partnerTenantId: false,
        }}
      />
    </Box>
  );
}
